import { useNavigate, useLocation } from "react-router-dom";
import { FileText, Plus, ClipboardList, Truck, LogOut, User, BarChart3, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { invoiceApi, agreementApi, vehicleApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// Company logo URL
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_rentflow-67/artifacts/zjs6jlz1_ChatGPT%20Image%20Feb%201%2C%202026%2C%2007_47_29%20PM.png";

export const AppHeader = ({ onCreateInvoice, onCreateAgreement, onCreateVehicle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    toast.success("Logged out", {
      description: "You have been logged out successfully."
    });
    navigate('/login');
  };

  const handleCreateInvoice = async () => {
    if (onCreateInvoice) {
      onCreateInvoice();
    } else {
      try {
        const newInvoice = await invoiceApi.createInvoice();
        toast.success("Invoice Created", {
          description: `New invoice ${newInvoice.id} has been created.`
        });
        navigate(`/invoice/${newInvoice.id}?edit=true`);
      } catch (error) {
        toast.error("Failed to create invoice", {
          description: "Please try again."
        });
      }
    }
  };

  const handleCreateAgreement = async () => {
    if (onCreateAgreement) {
      onCreateAgreement();
    } else {
      try {
        const newAgreement = await agreementApi.createAgreement();
        toast.success("Agreement Created", {
          description: `New agreement ${newAgreement.id} has been created.`
        });
        navigate(`/agreement/${newAgreement.id}?edit=true`);
      } catch (error) {
        toast.error("Failed to create agreement", {
          description: "Please try again."
        });
      }
    }
  };

  const handleCreateVehicle = async () => {
    if (onCreateVehicle) {
      onCreateVehicle();
    } else {
      try {
        const newVehicle = await vehicleApi.createVehicle();
        toast.success("Vehicle Created", {
          description: `New vehicle ${newVehicle.id} has been created.`
        });
        navigate(`/vehicle/${newVehicle.id}?edit=true`);
      } catch (error) {
        toast.error("Failed to create vehicle", {
          description: "Please try again."
        });
      }
    }
  };

  const isOnDashboard = location.pathname === '/dashboard';
  const isOnRevenue = location.pathname === '/revenue';
  const isOnInvoices = location.pathname === '/invoices' || location.pathname.startsWith('/invoice/');
  const isOnAgreements = location.pathname === '/agreements' || location.pathname.startsWith('/agreement/');
  const isOnVehicles = location.pathname === '/vehicles' || location.pathname.startsWith('/vehicle/');

  const getCreateAction = () => {
    if (isOnVehicles) {
      return { label: "Add Vehicle", action: handleCreateVehicle };
    } else if (isOnAgreements) {
      return { label: "New Agreement", action: handleCreateAgreement };
    } else {
      return { label: "New Invoice", action: handleCreateInvoice };
    }
  };

  const createAction = getCreateAction();

  return (
    <header className="border-b border-border bg-card sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            <img 
              src={COMPANY_LOGO} 
              alt="Ak Car Rental" 
              className="h-10 w-auto object-contain rounded-lg"
            />
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">Ak Car Rental</h1>
              <p className="text-xs text-muted-foreground">Management System</p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <nav className="flex items-center gap-1">
            <Button 
              variant={isOnDashboard ? "secondary" : "ghost"} 
              size="sm" 
              className="gap-2"
              onClick={() => navigate('/dashboard')}
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Button>
            <Button 
              variant={isOnRevenue ? "secondary" : "ghost"} 
              size="sm" 
              className="gap-2"
              onClick={() => navigate('/revenue')}
            >
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Revenue</span>
            </Button>
            <Button 
              variant={isOnInvoices ? "secondary" : "ghost"} 
              size="sm" 
              className="gap-2"
              onClick={() => navigate('/invoices')}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Invoices</span>
            </Button>
            <Button 
              variant={isOnAgreements ? "secondary" : "ghost"} 
              size="sm" 
              className="gap-2"
              onClick={() => navigate('/agreements')}
            >
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">Agreements</span>
            </Button>
            <Button 
              variant={isOnVehicles ? "secondary" : "ghost"} 
              size="sm" 
              className="gap-2"
              onClick={() => navigate('/vehicles')}
            >
              <Truck className="w-4 h-4" />
              <span className="hidden sm:inline">Vehicles</span>
            </Button>
            
            {/* Create button */}
            {!isOnDashboard && !isOnRevenue && (
              <>
                <div className="w-px h-6 bg-border mx-2" />
                <Button 
                  variant="default" 
                  size="sm" 
                  className="gap-2"
                  onClick={createAction.action}
                  data-testid="create-btn"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{createAction.label}</span>
                </Button>
              </>
            )}
            
            {/* User Menu */}
            <div className="w-px h-6 bg-border mx-2" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-btn">
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="hidden md:inline text-sm max-w-[100px] truncate">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </div>
    </header>
  );
};
